import { PerspectiveCamera } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { Lighting } from "../../render/Lighting";
import { Renderer } from "../../render/Renderer";
import { World } from "../../render/world/World";
import type { ModelInfo } from "../model/modelInfo";
import type { ModelLook } from "../model/modelLook";
import { parseGltfFile } from "./modelLoader";
import { ViewerBoardPatch } from "./ViewerBoardPatch";
import { ViewerModel } from "./ViewerModel";
import { DEFAULT_LOOK, LOOK_AT_HEIGHT, SHADOW_AREA, VIEWER_LIGHTING } from "../viewerConstants";

export class ModelViewport {
  private readonly camera = new PerspectiveCamera(35, 1, 0.01, 200);
  private readonly world = new World();
  private readonly renderer: Renderer;
  private readonly lighting: Lighting;
  private readonly controls: OrbitControls;
  private readonly boardPatch = new ViewerBoardPatch();
  private model: ViewerModel | null = null;
  private latestLoad = 0;
  private look = DEFAULT_LOOK;
  private clipName: string | null = null;

  constructor(container: HTMLElement, background: string) {
    this.camera.position.set(2.2, 1.6, 2.2);
    this.world.setBackgroundColor(background);
    this.world.add(this.boardPatch);
    this.renderer = new Renderer(container, this.world, this.camera);

    this.lighting = new Lighting(this.world, this.renderer, VIEWER_LIGHTING);
    this.lighting.setFocusArea(SHADOW_AREA);

    this.controls = new OrbitControls(this.camera, this.renderer.canvas);
    this.controls.target.set(0, LOOK_AT_HEIGHT, 0);
    this.controls.enableDamping = true;

    this.renderer.onFrame(() => this.controls.update());
  }

  // NOTE: resolves null when a newer file was dropped before this one finished loading
  async loadModel(file: File): Promise<ModelInfo | null> {
    const load = ++this.latestLoad;
    const gltf = await parseGltfFile(file).catch((error: unknown) => {
      if (load === this.latestLoad) this.removeModel();
      throw error;
    });
    const model = new ViewerModel(gltf);
    if (load !== this.latestLoad) {
      model.dispose();
      return null;
    }

    this.removeModel();
    this.model = model;
    this.world.add(model);
    this.applyLook();
    model.playClip(this.clipName);
    return model.info;
  }

  setLook(look: ModelLook) {
    const materialsChanged = look.materials !== this.look.materials;
    this.look = look;
    this.model?.setTransform(look.scale, look.rotationYDegrees);
    if (materialsChanged) this.model?.setMaterials(look.materials);
  }

  playClip(clipName: string | null) {
    this.clipName = clipName;
    this.model?.playClip(clipName);
  }

  setBloomStrength(strength: number) {
    this.renderer.setBloomStrength(strength);
  }

  dispose() {
    this.latestLoad += 1;
    this.controls.dispose();
    this.lighting.dispose();
    this.world.dispose();
    this.renderer.dispose();
  }

  private applyLook() {
    this.model?.setTransform(this.look.scale, this.look.rotationYDegrees);
    this.model?.setMaterials(this.look.materials);
  }

  private removeModel() {
    if (this.model) this.world.removeAndDispose(this.model);
    this.model = null;
  }
}
